import React from "react";
import ReactDOM from "react-dom";
import * as jsxDevRuntime from "react/jsx-dev-runtime";
import * as jsxRuntime from "react/jsx-runtime";

/**
 * Webpack Module Federation expects __webpack_init_sharing__ / __webpack_share_scopes__
 * on the host. Vite does not provide these — register host singletons so Webpack remotes
 * reuse the same React instance (required for hooks / context).
 */
type ShareScopeEntry = {
  get: () => Promise<() => unknown>;
  loaded: 1;
  from: string;
  eager: true;
};

type ShareScope = Record<string, Record<string, ShareScopeEntry>>;

const HOST_ID = "tc-web-app-service";

export function registerHostSharedModule(
  scope: ShareScope,
  packageName: string,
  version: string,
  moduleExport: unknown
): void {
  if (!scope[packageName]) {
    scope[packageName] = {};
  }

  if (scope[packageName][version]?.loaded) {
    return;
  }

  scope[packageName][version] = {
    get: () => Promise.resolve(() => moduleExport),
    loaded: 1,
    from: HOST_ID,
    eager: true
  };
}

function registerHostReactSingletons(scope: ShareScope): void {
  registerHostSharedModule(scope, "react", React.version, React);
  registerHostSharedModule(scope, "react-dom", ReactDOM.version, ReactDOM);
  registerHostSharedModule(scope, "react/jsx-runtime", React.version, jsxRuntime);

  if (process.env.NODE_ENV !== "production") {
    registerHostSharedModule(
      scope,
      "react/jsx-dev-runtime",
      React.version,
      jsxDevRuntime
    );
  }
}

export async function ensureWebpackShareScope(): Promise<Record<string, unknown>> {
  const globalScope = globalThis as typeof globalThis & {
    __webpack_init_sharing__?: (scope: string) => Promise<void>;
    __webpack_share_scopes__?: { default: ShareScope };
  };

  if (typeof globalScope.__webpack_init_sharing__ !== "function") {
    globalScope.__webpack_init_sharing__ = async () => undefined;
  }

  if (!globalScope.__webpack_share_scopes__) {
    globalScope.__webpack_share_scopes__ = { default: {} };
  }

  const shareScope = globalScope.__webpack_share_scopes__.default;
  registerHostReactSingletons(shareScope);

  await globalScope.__webpack_init_sharing__("default");
  return shareScope;
}
